import { createSelector } from '@reduxjs/toolkit'
import format from 'date-fns/format'

import { BILLING_PERIOD_SUPPORTED_VIEW } from 'settings/constants/billing'
import { createDateFromString } from 'utils/date'
import { get } from 'utils/lodash'

const YEAR_FORMAT = 'yyyy'
const MONTH_FORMAT = 'MMMM'
const MONTH_SHORT_FORMAT = 'MMM'
const DAY_FORMAT = 'd'
const MONTH_DAY_FORMAT = `${MONTH_SHORT_FORMAT} ${DAY_FORMAT}`
const MONTH_YEAR_FORMAT = `${MONTH_SHORT_FORMAT} ${YEAR_FORMAT}`
const US_DATE_FORMAT = 'M/d/yyyy'

const { MONTH, QUARTER, BI_ANNUALLY, TWENTY_EIGHT_DAYS, DEFAULT } = BILLING_PERIOD_SUPPORTED_VIEW

// Mirrors the backend TwentyEightDayBillingScheduleGenerator (Jan-1 anchored, leap-invariant);
// a copy lives in file-generator/utils/date.ts — keep the three in sync
export const twentyEightDayPeriodMap = {
  '1/1': '1',
  '1/29': '2',
  '2/26': '3',
  '3/26': '4',
  '4/23': '5',
  '5/21': '6',
  '6/18': '7',
  '7/16': '8',
  '8/13': '9',
  '9/10': '10',
  '10/8': '11',
  '11/5': '12',
  '12/3': '13',
}

export const getSupportedViewType = (billingProfile = {}) => {
  const { interval, intervalType } = get(billingProfile, 'billingCycle', {})
  const { timing, timingDay } = billingProfile
  const isMonthlyCycle = intervalType === 'Month'
  const isQuarterlyCycle = intervalType === 'Quarter'
  const isDailyCycle = intervalType === 'Day'

  if (timing !== 'Day' || parseInt(timingDay, 10) !== 1) {
    return DEFAULT
  }

  if (interval === 1 && isMonthlyCycle) {
    return MONTH
  }

  if ((interval === 1 && isQuarterlyCycle) || (interval === 3 && isMonthlyCycle)) {
    return QUARTER
  }

  if ((interval === 2 && isQuarterlyCycle) || (interval === 6 && isMonthlyCycle)) {
    return BI_ANNUALLY
  }

  if (interval === 28 && isDailyCycle) {
    return TWENTY_EIGHT_DAYS
  }

  return DEFAULT
}

const getSupportedViewData = (viewType, period) => {
  const startDate = createDateFromString(get(period, 'startDate'))
  const endDate = createDateFromString(get(period, 'endDate'))
  const invoiceDate = get(period, 'invoiceDate')
  const locked = get(period, 'locked', false)

  switch (viewType) {
    case MONTH:
      return {
        invoiceDate,
        labelLeft: format(startDate, `${MONTH_FORMAT} ${YEAR_FORMAT}`),
        labelRight: `${format(startDate, MONTH_DAY_FORMAT)} - ${format(endDate, DAY_FORMAT)}`,
        locked,
      }

    case QUARTER:
      return {
        invoiceDate,
        labelLeft: format(startDate, `QQQ ${YEAR_FORMAT}`),
        labelRight: `${format(startDate, MONTH_DAY_FORMAT)} - ${format(endDate, MONTH_DAY_FORMAT)}`,
        locked,
      }

    case TWENTY_EIGHT_DAYS: {
      const dateKey = startDate instanceof Date ? `${startDate.getMonth() + 1}/${startDate.getDate()}` : ''
      // Periods outside the mapped cycle year (e.g. paginated older/newer pages) fall back to a date label
      const periodNumber = twentyEightDayPeriodMap[dateKey]
      return {
        invoiceDate,
        labelLeft: periodNumber ? `Period ${periodNumber}` : format(startDate, MONTH_YEAR_FORMAT),
        labelRight: `${format(startDate, US_DATE_FORMAT)} - ${format(endDate, US_DATE_FORMAT)}`,
        locked,
      }
    }

    // Annual, Semi-Annual, or Other
    default:
      return {
        invoiceDate,
        labelLeft: `${format(startDate, MONTH_YEAR_FORMAT)} - ${format(endDate, MONTH_YEAR_FORMAT)}`,
        labelRight: `${format(createDateFromString(invoiceDate), 'do')}`,
        locked,
      }
  }
}

export const selectBillingProfileById = createSelector([state => state?.billingProfiles, state => state?.id], (billingProfiles = [], id) =>
  billingProfiles.find(profile => profile.id === id)
)

// Plain pure function (NOT createSelector): every caller passes a fresh ad-hoc object (often
// with an inline array literal), so lru(1) memoization could never hit and only thrashed the
// single shared cache across billing/customers/WO-detail callers.
/**
 * @param {{ billingProfile?: object, billingPeriods?: Array<object> }} [args]
 * @returns {Array<{ invoiceDate?: *, labelLeft?: string, labelRight?: string, locked?: boolean }>}
 */
export const getBillingPeriods = ({ billingProfile, billingPeriods = [] } = {}) => {
  // If no matched profile and no periods then return []
  if (!billingProfile || !Array.isArray(billingPeriods) || !billingPeriods.length) return []

  const viewType = getSupportedViewType(billingProfile)
  return billingPeriods.map(period => getSupportedViewData(viewType, period))
}
